// Physical Asset Compliance Management Dashboard
// UK HSE & British Standards Compliance (PUWER, LOLER, PAT, EICR, MOT, Fire Safety, Calibration)
// REDESIGNED with Modern UX Principles

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  HelpCircle,
  Search,
  Download,
  Filter,
  TrendingUp,
  TrendingDown,
  Info,
  ExternalLink,
  FileText,
  Settings as SettingsIcon,
  Shield,
  AlertTriangle,
  Calendar,
  BarChart3,
  Zap,
  Target,
  Activity,
  ChevronRight,
  X,
  Sparkles,
  Bell,
  Users,
  MapPin,
  Package,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { ComplianceSummary, AssetComplianceListItem, ComplianceFilters } from '@/types/compliance';
import { mockComplianceSummary, mockAssetComplianceList } from '@/lib/mock-compliance-data';
import { getRegimeInfo, getStatusConfig, getRiskBandConfig } from '@/lib/compliance-data';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';

export default function CompliancePage(): React.ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [assetList, setAssetList] = useState<AssetComplianceListItem[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<AssetComplianceListItem[]>([]);
  const [filters, setFilters] = useState<ComplianceFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        // TODO: Replace with actual API calls when backend is ready
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
        setSummary(mockComplianceSummary);
        setAssetList(mockAssetComplianceList);
        setFilteredAssets(mockAssetComplianceList);
      } catch (error) {
        console.error('Failed to load compliance data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...assetList];

    if (filters.status) {
      filtered = filtered.filter(a => a.status === filters.status);
    }
    if (filters.regimeCode) {
      filtered = filtered.filter(a => a.regimeCode === filters.regimeCode);
    }
    if (filters.locationId) {
      filtered = filtered.filter(a => a.location === filters.locationId);
    }
    if (filters.category) {
      filtered = filtered.filter(a => a.category === filters.category);
    }
    if (filters.riskBand) {
      filtered = filtered.filter(a => a.riskBand === filters.riskBand);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.assetName.toLowerCase().includes(query) ||
        a.assetId.toLowerCase().includes(query) ||
        a.regimeName.toLowerCase().includes(query)
      );
    }

    setFilteredAssets(filtered);
  }, [filters, searchQuery, assetList]);

  // Calculate upcoming and overdue items
  const upcomingItems = useMemo(() => {
    return assetList
      .filter(a => a.status === 'due-soon')
      .sort((a, b) => {
        const daysA = a.daysUntilDue ?? Infinity;
        const daysB = b.daysUntilDue ?? Infinity;
        return daysA - daysB;
      })
      .slice(0, 10);
  }, [assetList]);

  const overdueItems = useMemo(() => {
    return assetList
      .filter(a => a.status === 'overdue')
      .sort((a, b) => {
        const daysA = a.daysUntilDue ?? 0;
        const daysB = b.daysUntilDue ?? 0;
        return daysA - daysB; // Most overdue first (most negative)
      })
      .slice(0, 10);
  }, [assetList]);

  // AI Insights
  const insights = useMemo(() => {
    if (!summary) return [];
    
    const result: Array<{
      type: string;
      icon: React.ComponentType<{ className?: string }>;
      title: string;
      description: string;
      action: string;
      onClick: () => void;
    }> = [];
    
    // Critical overdue items
    if (summary.nonCompliantAssets > 0) {
      result.push({
        type: 'critical',
        icon: AlertTriangle,
        title: 'Immediate Action Required',
        description: `${summary.nonCompliantAssets} assets are overdue for compliance checks. Prioritize high-risk items.`,
        action: 'View Overdue',
        onClick: () => setFilters({ status: 'overdue' })
      });
    }
    
    // Upcoming deadlines
    if (summary.dueSoonAssets > 5) {
      result.push({
        type: 'warning',
        icon: Clock,
        title: 'Plan Ahead',
        description: `${summary.dueSoonAssets} compliance checks due in the next 30 days. Schedule inspections now.`,
        action: 'View Schedule',
        onClick: () => setActiveTab('upcoming')
      });
    }
    
    // Performance improvement
    if (summary.trend90Days > 0) {
      result.push({
        type: 'success',
        icon: TrendingUp,
        title: 'Improving Compliance',
        description: `Compliance rate improved by ${summary.trend90Days}% over the last 90 days. Keep up the good work!`,
        action: 'View Trends',
        onClick: () => setActiveTab('insights')
      });
    }
    
    // Low performing regulation
    const lowPerformingKPI = summary.kpis.find(kpi => kpi.complianceRate < kpi.target);
    if (lowPerformingKPI) {
      result.push({
        type: 'info',
        icon: Target,
        title: `${lowPerformingKPI.regimeCode} Below Target`,
        description: `${lowPerformingKPI.regimeName} compliance at ${lowPerformingKPI.complianceRate.toFixed(1)}% (target: ${lowPerformingKPI.target}%). Focus resources here.`,
        action: 'View Details',
        onClick: () => setFilters({ regimeCode: lowPerformingKPI.regimeCode })
      });
    }
    
    return result;
  }, [summary]);

  const handleExport = () => {
    console.log('Export compliance report');
  };

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets?id=${assetId}`);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const hasActiveFilters = Object.keys(filters).length > 0 || searchQuery.length > 0;

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b bg-gradient-to-r from-primary/10 via-primary/5 to-background p-6">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="flex-1 overflow-auto p-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="pb-3">
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-96">
          <CardContent className="pt-6">
            <div className="text-center text-red-500">
              <AlertCircle className="mx-auto h-12 w-12 mb-4" />
              <p>Failed to load compliance data</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Compliance Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Physical asset compliance management for UK HSE & British Standards
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/compliance/settings')}>
              <SettingsIcon className="mr-2 h-4 w-4" />
              Settings
            </Button>
            <Button onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export Report
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 space-y-6">
          {/* Enhanced Summary Cards with Progress Bars */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="relative overflow-hidden border-l-4 border-l-green-500 hover:shadow-lg transition-all duration-300 cursor-pointer group" onClick={() => setFilters({ status: 'compliant' })}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Compliant Assets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-bold text-green-600">{summary.compliantAssets}</div>
                    <div className="text-sm text-muted-foreground">/ {summary.totalAssets}</div>
                  </div>
                  <Progress value={(summary.compliantAssets / summary.totalAssets) * 100} className="h-2" />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {((summary.compliantAssets / summary.totalAssets) * 100).toFixed(1)}% of total
                    </span>
                    <div className="flex items-center gap-1 text-green-600 font-medium">
                      <TrendingUp className="h-3 w-3" />
                      <span>+{summary.trend90Days}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-l-4 border-l-red-500 hover:shadow-lg transition-all duration-300 cursor-pointer group" onClick={() => setFilters({ status: 'overdue' })}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  Overdue Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-bold text-red-600">{summary.nonCompliantAssets}</div>
                    <div className="text-sm text-muted-foreground">urgent</div>
                  </div>
                  <Progress value={(summary.nonCompliantAssets / summary.totalAssets) * 100} className="h-2 [&>div]:bg-red-500" />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {((summary.nonCompliantAssets / summary.totalAssets) * 100).toFixed(1)}% of total
                    </span>
                    <div className="flex items-center gap-1 text-red-600 font-medium">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Action needed</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-l-4 border-l-yellow-500 hover:shadow-lg transition-all duration-300 cursor-pointer group" onClick={() => setFilters({ status: 'due-soon' })}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  Due Soon (30 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-bold text-yellow-600">{summary.dueSoonAssets}</div>
                    <div className="text-sm text-muted-foreground">upcoming</div>
                  </div>
                  <Progress value={(summary.dueSoonAssets / summary.totalAssets) * 100} className="h-2 [&>div]:bg-yellow-500" />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {((summary.dueSoonAssets / summary.totalAssets) * 100).toFixed(1)}% of total
                    </span>
                    <div className="flex items-center gap-1 text-yellow-600 font-medium">
                      <Calendar className="h-3 w-3" />
                      <span>Schedule now</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-l-4 border-l-primary hover:shadow-lg transition-all duration-300 group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                  <Target className="h-4 w-4 text-primary" />
                  Overall Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-bold text-primary">{summary.compliancePercentage.toFixed(1)}%</div>
                    <div className="text-sm text-muted-foreground">score</div>
                  </div>
                  <Progress value={summary.compliancePercentage} className="h-2" />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Target: 95%</span>
                    <div className={cn(
                      "flex items-center gap-1 font-medium",
                      summary.compliancePercentage >= 95 ? "text-green-600" : "text-yellow-600"
                    )}>
                      {summary.compliancePercentage >= 95 ? (
                        <>
                          <CheckCircle className="h-3 w-3" />
                          <span>On target</span>
                        </>
                      ) : (
                        <>
                          <Target className="h-3 w-3" />
                          <span>{(95 - summary.compliancePercentage).toFixed(1)}% to go</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Insights Section */}
          {insights.length > 0 && (
            <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-background">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Smart Insights & Recommendations
                </CardTitle>
                <CardDescription>
                  AI-powered analysis of your compliance data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2">
                  {insights.map((insight, idx) => {
                    const Icon = insight.icon;
                    const colorClasses = {
                      critical: 'border-red-200 bg-red-50 dark:bg-red-950/20',
                      warning: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20',
                      success: 'border-green-200 bg-green-50 dark:bg-green-950/20',
                      info: 'border-blue-200 bg-blue-50 dark:bg-blue-950/20',
                    };
                    const iconColors = {
                      critical: 'text-red-600',
                      warning: 'text-yellow-600',
                      success: 'text-green-600',
                      info: 'text-blue-600',
                    };
                    
                    return (
                      <Card key={idx} className={cn("border-2 hover:shadow-md transition-all", colorClasses[insight.type as keyof typeof colorClasses])}>
                        <CardContent className="pt-6">
                          <div className="flex items-start gap-3">
                            <div className={cn("p-2 rounded-lg", iconColors[insight.type as keyof typeof iconColors])}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <h4 className="font-semibold text-sm">{insight.title}</h4>
                              <p className="text-xs text-muted-foreground">{insight.description}</p>
                              <Button 
                                variant="link" 
                                className="h-auto p-0 text-xs"
                                onClick={insight.onClick}
                              >
                                {insight.action} <ChevronRight className="h-3 w-3 ml-1" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
              <TabsTrigger value="overview" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="by-check" className="gap-2">
                <FileText className="h-4 w-4" />
                By Check Type
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="gap-2">
                <Calendar className="h-4 w-4" />
                Upcoming & Overdue
              </TabsTrigger>
              <TabsTrigger value="insights" className="gap-2">
                <Activity className="h-4 w-4" />
                Insights
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4 mt-6">
              {/* Compliance by Regulation Grid */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Compliance by Regulation Type
                  </CardTitle>
                  <CardDescription>
                    Click any regulation to filter assets and view details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {summary.kpis.map((kpi) => {
                      const regimeInfo = getRegimeInfo(kpi.regimeCode);
                      const isOnTarget = kpi.complianceRate >= kpi.target;
                      const complianceColor = kpi.complianceRate >= 90 ? 'green' : kpi.complianceRate >= 75 ? 'yellow' : 'red';
                      
                      return (
                        <TooltipProvider key={kpi.regimeCode}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Card 
                                className={cn(
                                  "cursor-pointer hover:shadow-lg transition-all duration-300 group relative overflow-hidden",
                                  !isOnTarget && "border-red-200"
                                )}
                                onClick={() => setFilters({ regimeCode: kpi.regimeCode })}
                              >
                                <div className={cn(
                                  "absolute top-0 right-0 w-24 h-24 rounded-full -mr-12 -mt-12 opacity-10 group-hover:scale-110 transition-transform",
                                  complianceColor === 'green' && "bg-green-500",
                                  complianceColor === 'yellow' && "bg-yellow-500",
                                  complianceColor === 'red' && "bg-red-500"
                                )} />
                                <CardHeader className="pb-3">
                                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                                    <span className="truncate">{kpi.regimeCode}</span>
                                    <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  </CardTitle>
                                  <CardDescription className="text-xs line-clamp-1">
                                    {kpi.regimeName}
                                  </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                  <div className="flex items-baseline gap-2">
                                    <span className={cn(
                                      "text-2xl font-bold",
                                      complianceColor === 'green' && "text-green-600",
                                      complianceColor === 'yellow' && "text-yellow-600",
                                      complianceColor === 'red' && "text-red-600"
                                    )}>
                                      {kpi.complianceRate.toFixed(0)}%
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      / {kpi.target}%
                                    </span>
                                  </div>
                                  <Progress 
                                    value={kpi.complianceRate} 
                                    className={cn(
                                      "h-2",
                                      complianceColor === 'green' && "[&>div]:bg-green-500",
                                      complianceColor === 'yellow' && "[&>div]:bg-yellow-500",
                                      complianceColor === 'red' && "[&>div]:bg-red-500"
                                    )}
                                  />
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground flex items-center gap-1">
                                      <Package className="h-3 w-3" />
                                      {kpi.totalAssets} assets
                                    </span>
                                    {kpi.breachCount > 0 && (
                                      <Badge variant="destructive" className="text-xs h-5">
                                        {kpi.breachCount} overdue
                                      </Badge>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-sm">
                              <div className="space-y-2">
                                <p className="font-semibold">{regimeInfo.name}</p>
                                <p className="text-xs">{regimeInfo.description}</p>
                                <div className="text-xs space-y-1 pt-2 border-t">
                                  <p><strong>Regulatory Body:</strong> {regimeInfo.regulatoryBody}</p>
                                  <p><strong>Legislation:</strong> {regimeInfo.legislationReference}</p>
                                  <a 
                                    href={regimeInfo.guidanceUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-primary hover:underline"
                                  >
                                    View official guidance <ExternalLink className="h-3 w-3" />
                                  </a>
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Charts Row */}
              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Compliance by Location
                    </CardTitle>
                    <CardDescription>Site-level performance overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={summary.byLocation}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey="locationName" 
                          tick={{ fontSize: 11 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <RechartsTooltip 
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Bar dataKey="complianceScore" name="Compliance %" radius={[8, 8, 0, 0]}>
                          {summary.byLocation.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.complianceScore >= 90 ? '#10b981' : entry.complianceScore >= 75 ? '#f59e0b' : '#ef4444'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Compliance by Asset Category
                    </CardTitle>
                    <CardDescription>Category-level performance overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={summary.byCategory}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          dataKey="category" 
                          tick={{ fontSize: 11 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <RechartsTooltip 
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Bar dataKey="complianceScore" name="Compliance %" radius={[8, 8, 0, 0]}>
                          {summary.byCategory.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.complianceScore >= 90 ? '#10b981' : entry.complianceScore >= 75 ? '#f59e0b' : '#ef4444'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* By Check Type Tab */}
            <TabsContent value="by-check" className="space-y-4 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Compliance Status by Regulation</CardTitle>
                  <CardDescription>Detailed breakdown of compliant, due soon, and overdue assets per regulation</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={summary.kpis}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="regimeCode" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <RechartsTooltip 
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Bar dataKey="compliant" name="Compliant" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="dueSoon" name="Due Soon" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="overdue" name="Overdue" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Regulation Details</CardTitle>
                  <CardDescription>Complete breakdown by regulation type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Regulation</TableHead>
                          <TableHead className="text-right">Total Assets</TableHead>
                          <TableHead className="text-right">Compliant</TableHead>
                          <TableHead className="text-right">Due Soon</TableHead>
                          <TableHead className="text-right">Overdue</TableHead>
                          <TableHead className="text-right">Compliance Rate</TableHead>
                          <TableHead className="text-right">Target</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.kpis.map((kpi) => {
                          const isOnTarget = kpi.complianceRate >= kpi.target;
                          return (
                            <TableRow 
                              key={kpi.regimeCode}
                              className="cursor-pointer hover:bg-accent"
                              onClick={() => setFilters({ regimeCode: kpi.regimeCode })}
                            >
                              <TableCell className="font-medium">
                                <div>
                                  <div className="font-semibold">{kpi.regimeCode}</div>
                                  <div className="text-xs text-muted-foreground">{kpi.regimeName}</div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">{kpi.totalAssets}</TableCell>
                              <TableCell className="text-right">
                                <span className="text-green-600 font-medium">{kpi.compliant}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-yellow-600 font-medium">{kpi.dueSoon}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-red-600 font-medium">{kpi.overdue}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span className={cn(
                                    "font-semibold",
                                    isOnTarget ? "text-green-600" : "text-red-600"
                                  )}>
                                    {kpi.complianceRate.toFixed(1)}%
                                  </span>
                                  {isOnTarget ? (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 text-red-600" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">{kpi.target}%</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Upcoming & Overdue Tab */}
            <TabsContent value="upcoming" className="space-y-4 mt-6">
              <div className="grid gap-4 lg:grid-cols-2">
                {/* Overdue Items */}
                <Card className="border-red-200">
                  <CardHeader className="bg-red-50 dark:bg-red-950/20">
                    <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <AlertCircle className="h-5 w-5" />
                      Overdue Items ({overdueItems.length})
                    </CardTitle>
                    <CardDescription>
                      Immediate action required - sorted by most overdue
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {overdueItems.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                        <p className="font-medium">No overdue items!</p>
                        <p className="text-sm">All compliance checks are up to date.</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-3">
                          {overdueItems.map((item) => (
                            <Card 
                              key={item.assetId}
                              className="cursor-pointer hover:shadow-md transition-all border-red-200 hover:border-red-300"
                              onClick={() => handleAssetClick(item.assetId)}
                            >
                              <CardContent className="pt-4">
                                <div className="space-y-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-sm">{item.assetName}</h4>
                                      <p className="text-xs text-muted-foreground">{item.assetId}</p>
                                    </div>
                                    <Badge variant="destructive" className="text-xs">
                                      {Math.abs(item.daysUntilDue || 0)} days overdue
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Check:</span>
                                      <p className="font-medium">{item.regimeCode}</p>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Location:</span>
                                      <p className="font-medium">{item.location}</p>
                                    </div>
                                  </div>
                                  {item.nextDueDate && (
                                    <div className="text-xs">
                                      <span className="text-muted-foreground">Was due:</span>
                                      <p className="font-medium text-red-600">
                                        {format(new Date(item.nextDueDate), 'dd MMM yyyy')}
                                      </p>
                                    </div>
                                  )}
                                  {item.assignedTo && (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Users className="h-3 w-3" />
                                      <span>{item.assignedTo}</span>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>

                {/* Upcoming Items */}
                <Card className="border-yellow-200">
                  <CardHeader className="bg-yellow-50 dark:bg-yellow-950/20">
                    <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                      <Clock className="h-5 w-5" />
                      Due Soon ({upcomingItems.length})
                    </CardTitle>
                    <CardDescription>
                      Due within 30 days - plan ahead
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {upcomingItems.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Calendar className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium">No upcoming items</p>
                        <p className="text-sm">Nothing due in the next 30 days.</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-3">
                          {upcomingItems.map((item) => (
                            <Card 
                              key={item.assetId}
                              className="cursor-pointer hover:shadow-md transition-all border-yellow-200 hover:border-yellow-300"
                              onClick={() => handleAssetClick(item.assetId)}
                            >
                              <CardContent className="pt-4">
                                <div className="space-y-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-sm">{item.assetName}</h4>
                                      <p className="text-xs text-muted-foreground">{item.assetId}</p>
                                    </div>
                                    <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700">
                                      {item.daysUntilDue} days left
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Check:</span>
                                      <p className="font-medium">{item.regimeCode}</p>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Location:</span>
                                      <p className="font-medium">{item.location}</p>
                                    </div>
                                  </div>
                                  {item.nextDueDate && (
                                    <div className="text-xs">
                                      <span className="text-muted-foreground">Due date:</span>
                                      <p className="font-medium text-yellow-600">
                                        {format(new Date(item.nextDueDate), 'dd MMM yyyy')}
                                      </p>
                                    </div>
                                  )}
                                  {item.assignedTo && (
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Users className="h-3 w-3" />
                                      <span>{item.assignedTo}</span>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="space-y-4 mt-6">
              <div className="grid gap-4 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Compliance Trend</CardTitle>
                    <CardDescription>90-day performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div className={cn(
                          "text-4xl font-bold mb-2",
                          summary.trend90Days > 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {summary.trend90Days > 0 ? '+' : ''}{summary.trend90Days}%
                        </div>
                        <p className="text-sm text-muted-foreground">Change in compliance rate</p>
                        <div className="mt-4">
                          {summary.trend90Days > 0 ? (
                            <div className="flex items-center justify-center gap-2 text-green-600">
                              <TrendingUp className="h-5 w-5" />
                              <span className="text-sm font-medium">Improving</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-red-600">
                              <TrendingDown className="h-5 w-5" />
                              <span className="text-sm font-medium">Declining</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Risk Distribution</CardTitle>
                    <CardDescription>Assets by risk level</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {['high', 'medium', 'low'].map((risk) => {
                        const count = assetList.filter(a => a.riskBand === risk).length;
                        const percentage = (count / assetList.length) * 100;
                        const colors = {
                          high: { bg: 'bg-red-500', text: 'text-red-600' },
                          medium: { bg: 'bg-yellow-500', text: 'text-yellow-600' },
                          low: { bg: 'bg-green-500', text: 'text-green-600' },
                        };
                        
                        return (
                          <div key={risk} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="capitalize font-medium">{risk} Risk</span>
                              <span className={cn("font-semibold", colors[risk as keyof typeof colors].text)}>
                                {count} ({percentage.toFixed(0)}%)
                              </span>
                            </div>
                            <Progress 
                              value={percentage} 
                              className={cn("h-2", `[&>div]:${colors[risk as keyof typeof colors].bg}`)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Stats</CardTitle>
                    <CardDescription>Key metrics at a glance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Total Assets</span>
                        <span className="text-lg font-bold">{summary.totalAssets}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Regulations</span>
                        <span className="text-lg font-bold">{summary.kpis.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Locations</span>
                        <span className="text-lg font-bold">{summary.byLocation.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Categories</span>
                        <span className="text-lg font-bold">{summary.byCategory.length}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Performance by Location & Category */}
              <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Locations</CardTitle>
                    <CardDescription>Locations with highest compliance rates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[...summary.byLocation]
                        .sort((a, b) => b.complianceScore - a.complianceScore)
                        .slice(0, 5)
                        .map((location, idx) => (
                          <div key={location.locationName} className="flex items-center gap-3">
                            <div className={cn(
                              "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                              idx === 0 && "bg-yellow-100 text-yellow-700",
                              idx === 1 && "bg-gray-100 text-gray-700",
                              idx === 2 && "bg-orange-100 text-orange-700",
                              idx > 2 && "bg-muted text-muted-foreground"
                            )}>
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{location.locationName}</span>
                                <span className="text-sm font-semibold text-green-600">
                                  {location.complianceScore.toFixed(1)}%
                                </span>
                              </div>
                              <Progress value={location.complianceScore} className="h-1.5" />
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Categories</CardTitle>
                    <CardDescription>Categories with highest compliance rates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[...summary.byCategory]
                        .sort((a, b) => b.complianceScore - a.complianceScore)
                        .slice(0, 5)
                        .map((category, idx) => (
                          <div key={category.category} className="flex items-center gap-3">
                            <div className={cn(
                              "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold",
                              idx === 0 && "bg-yellow-100 text-yellow-700",
                              idx === 1 && "bg-gray-100 text-gray-700",
                              idx === 2 && "bg-orange-100 text-orange-700",
                              idx > 2 && "bg-muted text-muted-foreground"
                            )}>
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{category.category}</span>
                                <span className="text-sm font-semibold text-green-600">
                                  {category.complianceScore.toFixed(1)}%
                                </span>
                              </div>
                              <Progress value={category.complianceScore} className="h-1.5" />
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Asset List with Enhanced Filters */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>All Assets ({filteredAssets.length})</CardTitle>
                  <CardDescription>
                    Detailed compliance status for all assets
                  </CardDescription>
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    Clear all filters
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Enhanced Filters */}
              <div className="grid gap-3 md:grid-cols-5 mb-4">
                <div className="relative md:col-span-2">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search assets, IDs, regulations..."
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={filters.status || 'all'} onValueChange={(value: string) => setFilters({ ...filters, status: value === 'all' ? undefined : value as any })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="compliant">✓ Compliant</SelectItem>
                    <SelectItem value="due-soon">⏰ Due Soon</SelectItem>
                    <SelectItem value="overdue">⚠️ Overdue</SelectItem>
                    <SelectItem value="unknown">❓ Unknown</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.regimeCode || 'all'} onValueChange={(value: string) => setFilters({ ...filters, regimeCode: value === 'all' ? undefined : value as any })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Regulation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Regulations</SelectItem>
                    {summary.kpis.map(kpi => (
                      <SelectItem key={kpi.regimeCode} value={kpi.regimeCode}>{kpi.regimeCode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.riskBand || 'all'} onValueChange={(value: string) => setFilters({ ...filters, riskBand: value === 'all' ? undefined : value as any })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Risk Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risk Levels</SelectItem>
                    <SelectItem value="high">🔴 High Risk</SelectItem>
                    <SelectItem value="medium">🟡 Medium Risk</SelectItem>
                    <SelectItem value="low">🟢 Low Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Enhanced Table */}
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Asset</TableHead>
                      <TableHead className="font-semibold">Category</TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold">Regulation</TableHead>
                      <TableHead className="font-semibold">Last Check</TableHead>
                      <TableHead className="font-semibold">Next Due</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Risk</TableHead>
                      <TableHead className="font-semibold">Assigned To</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Search className="h-12 w-12 mb-2" />
                            <p className="font-medium">No assets found</p>
                            <p className="text-sm">Try adjusting your filters or search query</p>
                            {hasActiveFilters && (
                              <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2">
                                Clear filters
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAssets.map((asset) => {
                        const statusConfig = getStatusConfig(asset.status);
                        const riskConfig = asset.riskBand ? getRiskBandConfig(asset.riskBand) : null;
                        const StatusIcon = statusConfig.icon === 'CheckCircle' ? CheckCircle :
                                          statusConfig.icon === 'Clock' ? Clock :
                                          statusConfig.icon === 'AlertCircle' ? AlertCircle : HelpCircle;

                        return (
                          <TableRow 
                            key={asset.assetId}
                            className="cursor-pointer hover:bg-accent/50 transition-colors"
                            onClick={() => handleAssetClick(asset.assetId)}
                          >
                            <TableCell className="font-medium">
                              <div>
                                <div>{asset.assetName}</div>
                                <div className="text-xs text-muted-foreground">{asset.assetId}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-normal">
                                {asset.category}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{asset.location}</TableCell>
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="secondary" className="cursor-help">
                                      {asset.regimeCode}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="font-semibold">{asset.regimeName}</p>
                                    <p className="text-xs">{asset.requirementName}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell className="text-sm">
                              {asset.lastEventDate ? format(new Date(asset.lastEventDate), 'dd MMM yyyy') : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {asset.nextDueDate ? (
                                <div>
                                  <div className="text-sm font-medium">
                                    {format(new Date(asset.nextDueDate), 'dd MMM yyyy')}
                                  </div>
                                  {asset.daysUntilDue !== undefined && (
                                    <div className={cn(
                                      "text-xs font-medium",
                                      asset.daysUntilDue < 0 ? "text-red-600" :
                                      asset.daysUntilDue <= 7 ? "text-yellow-600" :
                                      "text-muted-foreground"
                                    )}>
                                      {asset.daysUntilDue < 0 ? `${Math.abs(asset.daysUntilDue)}d overdue` :
                                       asset.daysUntilDue === 0 ? 'Due today' :
                                       `${asset.daysUntilDue}d remaining`}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusConfig.className}>
                                <StatusIcon className="mr-1 h-3 w-3" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {riskConfig ? (
                                <Badge variant="outline" className={riskConfig.className}>
                                  {riskConfig.label}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {asset.assignedTo ? (
                                <div className="flex items-center gap-1">
                                  <Users className="h-3 w-3 text-muted-foreground" />
                                  <span>{asset.assignedTo}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Unassigned</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
